import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { SymbolMark } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Form } from '@/components/ui/form'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { SettingsFormFooter } from '@/containers/Settings/SettingsLayout'
import API from '@/lib/api'
import { Currency } from '@/types/Currency'

const schema = z.object({
  primaryCurrency: z.string(),
  alternativeCurrencies: z.array(z.string()),
})

type CurrenciesFormData = z.infer<typeof schema>

export function CurrenciesForm({
  currencies,
  defaultValues,
}: {
  currencies: Currency[]
  defaultValues?: CurrenciesFormData
}) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })

  const form = useForm<CurrenciesFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      primaryCurrency: defaultValues?.primaryCurrency || '',
      alternativeCurrencies: defaultValues?.alternativeCurrencies || [],
    },
  })

  const onSubmit = (data: CurrenciesFormData) =>
    API.patch('/api/v2/settings', {
      primaryCurrency: data.primaryCurrency,
      alternativeCurrencies: data.alternativeCurrencies,
    })
      .then(() => {
        toast.success(t('invoicing.generic.success'))
        form.reset(data)
      })
      .catch(() => {
        toast.error('Měny se nepodařilo uložit.')
      })

  const primaryCurrency = form.watch('primaryCurrency')
  const alternativeCurrencies = form.watch('alternativeCurrencies')

  const setPrimary = (value: string) => {
    form.setValue('primaryCurrency', value, {
      shouldDirty: true,
      shouldTouch: true,
    })
    if (alternativeCurrencies.includes(value)) {
      form.setValue(
        'alternativeCurrencies',
        alternativeCurrencies.filter((currency) => currency !== value),
        { shouldDirty: true, shouldTouch: true },
      )
    }
  }

  const toggleAlternative = (code: string, checked: boolean) => {
    form.setValue(
      'alternativeCurrencies',
      checked
        ? [...alternativeCurrencies, code]
        : alternativeCurrencies.filter((c) => c !== code),
      { shouldDirty: true, shouldTouch: true },
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <RadioGroup
          value={primaryCurrency}
          onValueChange={setPrimary}
          className="gap-0 overflow-hidden rounded-lg border"
          aria-label="Hlavní měna"
        >
          <div className="grid h-9 grid-cols-[minmax(0,1fr)_64px_64px] sm:grid-cols-[minmax(0,1fr)_96px_96px] items-center border-b px-4 text-[11px] font-medium tracking-[0.04em] text-muted-foreground uppercase">
            <span>Měna</span>
            <span className="text-center">Hlavní</span>
            <span className="text-center">Další</span>
          </div>
          {currencies.map((currency) => {
            const code = currency.code!
            const isPrimary = code === primaryCurrency
            return (
              <div
                key={code}
                className="grid min-h-11 grid-cols-[minmax(0,1fr)_64px_64px] sm:grid-cols-[minmax(0,1fr)_96px_96px] items-center border-b px-4 last:border-b-0"
              >
                <label
                  htmlFor={`primary-${code}`}
                  className="flex min-w-0 items-center gap-3 py-2 text-sm"
                >
                  <SymbolMark symbol={currency.symbol || code} />
                  <span className="flex min-w-0 flex-col sm:flex-row sm:items-baseline sm:gap-2">
                    <span className="truncate">{currency.name}</span>
                    <span className="text-xs whitespace-nowrap text-muted-foreground tabular">
                      {code}
                      {currency.symbol ? `, ${currency.symbol}` : ''}
                    </span>
                  </span>
                </label>
                <span className="flex justify-center">
                  <RadioGroupItem
                    value={code}
                    id={`primary-${code}`}
                    aria-label={`${currency.name} jako hlavní měna`}
                  />
                </span>
                <span className="flex justify-center">
                  <Checkbox
                    id={`alternative-${code}`}
                    aria-label={`${currency.name} jako další měna`}
                    disabled={isPrimary}
                    checked={isPrimary || alternativeCurrencies.includes(code)}
                    onCheckedChange={(checked) =>
                      toggleAlternative(code, checked === true)
                    }
                  />
                </span>
              </div>
            )
          })}
        </RadioGroup>

        <SettingsFormFooter hint="Hlavní měna je vždy k dispozici i jako další.">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {tg('button.save')}
          </Button>
        </SettingsFormFooter>
      </form>
    </Form>
  )
}
